<template>
  <div class="itv" :class="{ 'itv--embedded': embedded }">
    <div class="itv-modes" role="tablist" aria-label="Acciones con la prueba">
      <button
        v-for="tab in modeTabs"
        :key="tab.id"
        type="button"
        class="itv-mode"
        :class="{ 'itv-mode--active': activeMode === tab.id }"
        role="tab"
        :aria-selected="activeMode === tab.id"
        @click="onModeClick(tab)"
      >
        <span class="itv-mode-num">{{ tab.num }}</span>
        <span class="itv-mode-copy">
          <strong>{{ tab.title }}</strong>
          <small>{{ tab.subtitle }}</small>
        </span>
      </button>
    </div>

    <!-- Balance for the modes that spend a use (corregir / material de clase). -->
    <p v-if="usesNotice" class="itv-uses" v-html="usesNotice"></p>

    <div
      v-if="activeMode !== 'class-material'"
      class="itv-toolbar"
      aria-label="Filtros de prueba"
    >
      <label class="itv-field">
        <span class="itv-field-label">Curso</span>
        <div class="itv-select-wrap">
          <select
            :value="gradeKey"
            class="itv-select"
            @change="onGradeSelect"
          >
            <option value="" disabled>
              {{ loadingGrades ? "Cargando…" : "Selecciona un curso" }}
            </option>
            <option
              v-for="g in grades"
              :key="g.code"
              :value="String(g.code)"
            >
              {{ g.original_name }}
            </option>
          </select>
        </div>
      </label>

      <label class="itv-field">
        <span class="itv-field-label">Especialidad</span>
        <div class="itv-select-wrap">
          <select
            :value="subjectKey"
            class="itv-select"
            :disabled="!selectedGrade || loadingSubjects"
            @change="onSubjectSelect"
          >
            <option value="" disabled>
              {{
                !selectedGrade
                  ? "Elige un curso"
                  : loadingSubjects
                  ? "Cargando…"
                  : "Selecciona especialidad"
              }}
            </option>
            <option
              v-for="s in subjects"
              :key="s.enum_code"
              :value="String(s.enum_code)"
            >
              {{ s.name }}
            </option>
          </select>
        </div>
      </label>

      <label v-if="showTestSelector" class="itv-field">
        <span class="itv-field-label">Prueba</span>
        <div class="itv-select-wrap">
          <select
            v-model="materialModel"
            class="itv-select"
            :disabled="!materials.length || loadingMaterials"
            @change="onMaterialSelect"
          >
            <option value="" disabled>
              {{
                loadingMaterials
                  ? "Cargando…"
                  : materials.length
                  ? "Selecciona una prueba"
                  : "Sin pruebas disponibles"
              }}
            </option>
            <option
              v-for="m in materials"
              :key="m.id"
              :value="String(m.id)"
              :disabled="!m.pdf"
            >
              {{ materialLabel(m) }}
            </option>
          </select>
        </div>
      </label>

      <!-- Host-provided filters (e.g. redpie grade/student), placed alongside
           the curso/especialidad/prueba selects, only in the "Corregir" tab. -->
      <slot v-if="activeMode === 'correct'" name="toolbar-extra" />
    </div>

    <div
      v-if="loadingMaterials && activeMode !== 'class-material'"
      class="itv-muted"
    >
      Cargando materiales…
    </div>

    <template v-if="!loadingMaterials || activeMode === 'class-material'">
      <!-- Descargar -->
      <section
        v-show="activeMode === 'download'"
        class="itv-panel itv-panel--download"
        aria-label="Descargar prueba"
      >
        <div class="itv-download-action">
          <button
            type="button"
            class="itv-download-btn"
            :class="{ 'itv-pulse': selectedMaterial && selectedMaterial.pdf }"
            :disabled="!selectedMaterial || !selectedMaterial.pdf"
            @click="$emit('download', selectedMaterial)"
          >
            Descargar PDF gratis
          </button>
        </div>
        <div v-if="selectedMaterial && selectedMaterial.pdf" class="itv-preview">
          <span class="itv-preview-label">{{ previewLabel }}</span>
          <iframe
            :key="selectedMaterial.id"
            :src="previewSrc"
            class="itv-preview-frame"
            title="Vista previa del material"
          ></iframe>
        </div>
        <p v-else-if="selectedGrade && selectedSubject" class="itv-muted">
          No hay materiales descargables para esta especialidad y curso.
        </p>
      </section>

      <!-- Corregir -->
      <section
        v-show="activeMode === 'correct'"
        class="itv-panel itv-panel--correct"
        aria-label="Corregir prueba"
      >
        <div
          class="itv-embedded-panel"
          :class="{ 'itv-embedded-panel--guest': guestMode }"
        >
          <div class="itv-embedded-content">
            <slot name="correct">
              <p class="itv-muted">Selecciona una prueba para ver el formulario.</p>
            </slot>
          </div>
          <div v-if="guestMode" class="itv-guest-sticky">
            <p>
              Para <strong>corregir la prueba</strong> y generar el informe
              automático necesitas iniciar sesión.
            </p>
            <button
              type="button"
              class="itv-login-btn itv-pulse"
              @click="$emit('login')"
            >
              Inicia sesión para probarlo
            </button>
          </div>
        </div>
      </section>

      <!-- Material para la clase -->
      <section
        v-show="activeMode === 'class-material'"
        class="itv-panel itv-panel--class"
        aria-label="Material para la clase"
      >
        <div
          class="itv-embedded-panel"
          :class="{ 'itv-embedded-panel--guest': guestMode }"
        >
          <div class="itv-embedded-content">
            <slot name="class-material">
              <p class="itv-muted">Cargando generador de material…</p>
            </slot>
          </div>
          <div v-if="guestMode" class="itv-guest-sticky">
            <p>
              Para <strong>crear un material imprimible en PDF</strong> para tu
              clase necesitas iniciar sesión.
            </p>
            <button
              type="button"
              class="itv-login-btn itv-pulse"
              @click="$emit('login')"
            >
              Inicia sesión para probarlo
            </button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { usesLine, remainingUses } from "./serviceUses";
import { ensureReferralRewards } from "./referralRewards";

// Modes 2 and 3 spend `materials_uses` (ServiceLog INFORMAL vs MATERIAL);
// mode 1 (descargar) is free.
const MODES_WITH_USES = ["correct", "class-material"];

export default {
  name: "InformalTestsVitrina",
  props: {
    guestMode: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
    activeMode: {
      type: String,
      default: "download",
      validator: (v) => ["download", "correct", "class-material"].includes(v),
    },
    grades: { type: Array, default: () => [] },
    subjects: { type: Array, default: () => [] },
    materials: { type: Array, default: () => [] },
    selectedGrade: { type: Object, default: null },
    selectedSubject: { type: Object, default: null },
    selectedMaterial: { type: Object, default: null },
    loadingGrades: { type: Boolean, default: false },
    loadingSubjects: { type: Boolean, default: false },
    loadingMaterials: { type: Boolean, default: false },
    // Materiales para la clase product: only the IA material tab.
    classMaterialOnly: { type: Boolean, default: false },
  },
  created() {
    // `usesNotice` es un computed sobre `usesLine`, así que cuando la respuesta
    // llega el aviso se repinta solo — `rewardsByService` es observable.
    ensureReferralRewards("materials");
  },
  data() {
    return {
      // Local mirror for the material <select> so v-model keeps the DOM value
      // in sync across parent re-renders (a plain :value native select desyncs
      // and fires a spurious change with the empty placeholder).
      materialModel: "",
    };
  },
  watch: {
    materialKey: {
      immediate: true,
      handler(v) {
        this.materialModel = v;
      },
    },
  },
  computed: {
    // null (unknown balance / paid school) counts as "has uses" — never block
    // or nag on a number we are not sure about.
    hasUses() {
      const uses = remainingUses("materials");
      return uses === null || uses > 0;
    },
    usesNotice() {
      return MODES_WITH_USES.includes(this.activeMode)
        ? usesLine("materials")
        : "";
    },
    // Mobile browsers (Android Chrome, iOS Safari) refuse to render a PDF
    // inline in an iframe — they paint a "descargar/abrir" stub instead. The
    // PDFs live on public DO Spaces, so on mobile we hand the URL to Google's
    // viewer, which serves the pages as images and does render.
    // ponytail: gview is Google-hosted and can rate-limit; if it gets flaky,
    // generate a page-2 thumbnail server-side alongside the PDF.
    isMobile() {
      return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    },
    previewSrc() {
      const pdf = this.selectedMaterial && this.selectedMaterial.pdf;
      if (!pdf) return "";
      if (this.isMobile) {
        return (
          "https://docs.google.com/gview?embedded=true&url=" +
          encodeURIComponent(pdf)
        );
      }
      return pdf + "#toolbar=0&navpanes=0&view=FitH&page=2";
    },
    // gview ignores #page=2, so on mobile the preview opens on page 1.
    previewLabel() {
      return this.isMobile
        ? "Vista previa del material"
        : "Vista previa · segunda página";
    },
    modeTabs() {
      const all = [
        {
          id: "download",
          num: "1",
          title: "Ver y descargar prueba",
          subtitle: "Explora el material y descarga el PDF gratis.",
        },
        {
          id: "correct",
          num: "2",
          title: "Corregir informe",
          subtitle: "Completa el formulario y genera el informe automático.",
        },
        {
          id: "class-material",
          num: this.classMaterialOnly ? "1" : "3",
          title: "Material para la clase",
          subtitle: "Crea un bonito material imprimible en PDF para tu clase.",
        },
      ];
      if (this.classMaterialOnly) {
        return all.filter((t) => t.id === "class-material");
      }
      return all;
    },
    showTestSelector() {
      return this.materials.length > 1;
    },
    gradeKey() {
      return this.selectedGrade ? String(this.selectedGrade.code) : "";
    },
    subjectKey() {
      return this.selectedSubject ? String(this.selectedSubject.enum_code) : "";
    },
    materialKey() {
      return this.selectedMaterial ? String(this.selectedMaterial.id) : "";
    },
  },
  methods: {
    // Out of uses on a paid mode: let the host scroll down to the chat, where
    // the "get more uses" options are highlighted.
    onModeClick(tab) {
      this.$emit("mode-change", tab.id);
      if (MODES_WITH_USES.includes(tab.id) && !this.hasUses) {
        this.$emit("no-uses");
      }
    },
    materialLabel(m) {
      if (m.duration) return `${m.name} · ${m.duration} min`;
      return m.name;
    },
    onGradeSelect(event) {
      const code = Number(event.target.value);
      const grade = this.grades.find((g) => g.code === code) || null;
      this.$emit("grade-change", grade);
    },
    onSubjectSelect(event) {
      const enumCode = event.target.value;
      const subject =
        this.subjects.find((s) => String(s.enum_code) === enumCode) || null;
      this.$emit("subject-change", subject);
    },
    onMaterialSelect() {
      // Material ids are 9-char strings — compare as strings (Number() → NaN).
      const material =
        this.materials.find(
          (m) => String(m.id) === String(this.materialModel)
        ) || null;
      if (material) {
        this.$emit("material-change", material);
      } else {
        // Spurious empty/placeholder change (parent re-render) — restore the
        // real selection instead of dropping to the default.
        this.materialModel = this.materialKey;
      }
    },
  },
};
</script>

<style scoped>
.itv {
  --c: var(--main-color, #e84855);
  --rgb: var(--product-rgb, 232, 72, 85);
  --c-soft: var(--product-soft, var(--secondary-color, #fbe1e4));
  --c-deep: var(--product-deep, #9b1c28);
  --muted: #6b7280;
  font-family: "Ubuntu", Arial, sans-serif;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.itv-modes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 8px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.itv-uses {
  margin: -6px 0 14px;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--c-soft);
  color: var(--c-deep);
  font-size: 0.9rem;
  text-align: center;
  flex-shrink: 0;
}

.itv-mode {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  text-align: left;
  border: 2px dashed rgba(var(--rgb), 0.28);
  border-radius: 14px;
  background: #fff;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.itv-mode:hover {
  border-color: rgba(var(--rgb), 0.45);
  box-shadow: 0 4px 12px rgba(var(--rgb), 0.06);
}

.itv-mode--active {
  border-color: var(--c);
  border-style: solid;
  background: #fff;
  box-shadow: 0 6px 16px rgba(var(--rgb), 0.1);
}

.itv-mode-num {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #fff;
  color: var(--c-deep);
  font-size: 0.82rem;
  font-weight: 900;
  display: grid;
  place-items: center;
  border: 2px dashed rgba(var(--rgb), 0.35);
}

.itv-mode--active .itv-mode-num {
  background: var(--c);
  color: #fff;
  border-color: var(--c);
  border-style: solid;
}

.itv-mode-copy {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.itv-mode-copy strong {
  color: #111827;
  font-size: 0.92rem;
  line-height: 1.2;
}

.itv-mode-copy small {
  color: var(--muted);
  font-size: 0.76rem;
  line-height: 1.3;
  font-weight: 600;
}

.itv-toolbar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
  flex-shrink: 0;
}

.itv-field {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.itv-field-label {
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
  font-weight: 900;
}

.itv-select-wrap {
  position: relative;
}

.itv-select-wrap::after {
  content: "▾";
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--c);
  font-size: 0.78rem;
  pointer-events: none;
}

.itv-select {
  display: block;
  width: 100%;
  min-height: 40px;
  padding: 10px 34px 10px 12px;
  border: 2px dashed rgba(var(--rgb), 0.3);
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--c-deep);
  background: #fff;
  box-shadow: 0 8px 18px rgba(var(--rgb), 0.055);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}

.itv-select:focus {
  outline: none;
  border-color: var(--c);
  box-shadow: 0 0 0 4px rgba(var(--rgb), 0.1);
}

.itv-select:disabled {
  background: #f3f4f6;
  color: #9aa3af;
  cursor: not-allowed;
}

.itv-download-action {
  margin-bottom: 12px;
}

.itv-download-btn {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: 14px;
  background: var(--c);
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 16px 30px rgba(var(--rgb), 0.28);
}

.itv-download-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
  animation: none;
}

.itv-pulse {
  animation: itvPulse 1.35s ease-in-out infinite;
}

@keyframes itvPulse {
  0%,
  100% {
    box-shadow: 0 16px 30px rgba(var(--rgb), 0.28);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 20px 38px rgba(var(--rgb), 0.44);
    transform: scale(1.012);
  }
}

.itv-preview-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--muted);
  margin-bottom: 6px;
}

.itv-preview-frame {
  width: 100%;
  min-height: 480px;
  height: 52vh;
  max-height: 620px;
  border: 1px solid rgba(var(--rgb), 0.16);
  border-radius: 18px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.76),
    rgba(255, 255, 255, 0.54)
  );
  /* Scrollable: the visitor browses the whole material inside the frame
     (iOS needs momentum scrolling on the iframe itself). */
  -webkit-overflow-scrolling: touch;
}

.itv-embedded-panel {
  position: relative;
  border: 1px solid rgba(var(--rgb), 0.18);
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
}
.itv-panel {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.itv-panel--download {
  overflow-y: auto;
}
/* In the fixed-height shell (a granel) the score sheet must scroll, or the
   auto-advance leaves earlier answers out of reach. With no-inner-scroll
   (redpie) the panel grows and no scrollbar appears. */
.itv-panel--correct .itv-embedded-panel {
  overflow-y: auto;
}
.itv-panel--class .itv-embedded-panel {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}
.itv-panel--class .itv-embedded-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.itv-embedded-panel--guest .itv-embedded-content {
  pointer-events: none;
  user-select: none;
  padding-bottom: 4px;
}

.itv-embedded-content {
  padding: 8px 4px 0;
}

.itv-guest-sticky {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 16px;
  text-align: center;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.82) 0%,
    rgba(255, 255, 255, 0.98) 28%,
    #fff 100%
  );
  border-top: 1px dashed rgba(var(--rgb), 0.28);
  box-shadow: 0 -10px 24px rgba(var(--rgb), 0.08);
}

.itv-guest-sticky p {
  margin: 0;
  max-width: 420px;
  font-size: 0.88rem;
  color: #3a2a2d;
  line-height: 1.4;
}

.itv-guest-sticky strong {
  color: var(--c);
}

.itv-login-btn {
  border: 0;
  border-radius: 12px;
  background: var(--c);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 800;
  padding: 11px 20px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(var(--rgb), 0.26);
}

.itv-muted {
  font-size: 0.84rem;
  color: #9a8f91;
  margin: 8px 0;
}

@media (max-width: 640px) {
  .itv-modes {
    grid-template-columns: 1fr;
  }
  .itv-toolbar {
    grid-template-columns: 1fr;
  }
  .itv-preview-frame {
    min-height: 340px;
    height: 42vh;
  }
}
</style>
